package sarka.design.skoria.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import sarka.design.skoria.messaging.MessageError
import sarka.design.skoria.messaging.MessageRepository
import sarka.design.skoria.messaging.MessageView
import sarka.design.skoria.messaging.ReceiptType
import sarka.design.skoria.messaging.mergeMessages

data class ConversationUiState(
    val withUser: String = "",
    val messages: List<MessageView> = emptyList(),
    val input: String = "",
    val isLoading: Boolean = false,
    val isLoadingOlder: Boolean = false,
    val isSending: Boolean = false,
    val hasMore: Boolean = false,
    val error: String? = null,
    val socketConnected: Boolean = false,
    /**
     * Highest message time the other side has delivered/read up to. Kept as a
     * watermark rather than applied directly to messages, so a receipt that
     * arrives before the message it refers to (a real race: the send HTTP
     * response and the receipt push are independent) still takes effect once the
     * message lands. Both timestamps come from the server clock, so they're
     * comparable.
     */
    val deliveredUpTo: Long = 0L,
    val readUpTo: Long = 0L,
)

class ConversationViewModel(
    private val withUser: String,
    private val repository: MessageRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(ConversationUiState(withUser = withUser))
    val state: StateFlow<ConversationUiState> = _state.asStateFlow()

    init {
        loadInitial()
        repository.startListening()

        viewModelScope.launch {
            repository.incoming.collect { message ->
                // The socket carries every message for this user, not just this
                // conversation, so filter to the person on screen.
                val relevant = message.sender.equals(withUser, ignoreCase = true) ||
                    message.recipient.equals(withUser, ignoreCase = true)
                if (!relevant) return@collect

                val view = repository.decode(message, withUser)
                _state.update {
                    it.copy(messages = it.applyReceipts(mergeMessages(it.messages, listOf(view))))
                }

                // Arrived while the conversation is open, so it's already been seen.
                if (view.fromMe.not()) markRead()
            }
        }

        viewModelScope.launch {
            repository.socketConnected.collect { connected ->
                _state.update { it.copy(socketConnected = connected) }
            }
        }

        // Receipts for messages we sent to this person: flip Sent -> Delivered
        // -> Read live rather than only on a refetch.
        //
        // Recorded as a watermark and then applied, so a receipt that arrives
        // before the message it refers to still takes effect once that message
        // lands. Applying it directly here would miss that message: it wouldn't
        // be in the list yet, and receipts aren't replayed.
        viewModelScope.launch {
            repository.receipts.collect { receipt ->
                if (!receipt.withUser.equals(withUser, ignoreCase = true)) return@collect

                _state.update { current ->
                    val next = when (receipt.state) {
                        ReceiptType.READ -> current.copy(
                            deliveredUpTo = maxOf(current.deliveredUpTo, receipt.at),
                            readUpTo = maxOf(current.readUpTo, receipt.at),
                        )
                        ReceiptType.DELIVERED -> current.copy(
                            deliveredUpTo = maxOf(current.deliveredUpTo, receipt.at),
                        )
                        else -> current
                    }
                    next.copy(messages = next.applyReceipts(next.messages))
                }
            }
        }
    }

    /**
     * Decorates my own messages with delivered/read state from the watermarks.
     * A message counts as read/delivered when it was sent no later than the
     * corresponding watermark. Never downgrades a flag already set from a fetch.
     */
    private fun ConversationUiState.applyReceipts(messages: List<MessageView>): List<MessageView> {
        if (deliveredUpTo == 0L && readUpTo == 0L) return messages
        return messages.map { message ->
            if (!message.fromMe) return@map message
            val read = message.read || message.sentAt <= readUpTo
            val delivered = message.delivered || read || message.sentAt <= deliveredUpTo
            if (read == message.read && delivered == message.delivered) message
            else message.copy(delivered = delivered, read = read)
        }
    }

    private fun loadInitial() {
        _state.update { it.copy(isLoading = true, error = null) }

        viewModelScope.launch {
            repository.conversation(withUser)
                .onSuccess { page ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            messages = it.applyReceipts(mergeMessages(it.messages, page.messages)),
                            hasMore = page.hasMore,
                        )
                    }
                    markRead()
                }
                .onFailure { e ->
                    _state.update { it.copy(isLoading = false, error = e.readable()) }
                }
        }
    }

    /** Pages backwards from the oldest message currently held. */
    fun loadOlder() {
        val current = _state.value
        if (current.isLoadingOlder || !current.hasMore) return

        val oldest = current.messages.firstOrNull()?.sentAt ?: return
        _state.update { it.copy(isLoadingOlder = true) }

        viewModelScope.launch {
            repository.conversation(withUser, before = oldest)
                .onSuccess { page ->
                    _state.update {
                        it.copy(
                            isLoadingOlder = false,
                            messages = it.applyReceipts(mergeMessages(page.messages, it.messages)),
                            hasMore = page.hasMore,
                        )
                    }
                }
                .onFailure { e ->
                    _state.update { it.copy(isLoadingOlder = false, error = e.readable()) }
                }
        }
    }

    fun onInputChange(value: String) {
        _state.update { it.copy(input = value, error = null) }
    }

    fun send() {
        val text = _state.value.input.trim()
        if (text.isEmpty() || _state.value.isSending) return

        _state.update { it.copy(isSending = true, error = null) }

        viewModelScope.launch {
            repository.send(withUser, text)
                .onSuccess { sent ->
                    _state.update {
                        it.copy(
                            isSending = false,
                            input = "",
                            // Apply any receipt that arrived before this send's
                            // response landed, which is the race that left a
                            // read message showing as "Sent".
                            messages = it.applyReceipts(mergeMessages(it.messages, listOf(sent))),
                        )
                    }
                }
                .onFailure { e ->
                    // Input is deliberately preserved so the text isn't lost on
                    // a failed send.
                    _state.update { it.copy(isSending = false, error = e.readable()) }
                }
        }
    }

    private fun markRead() {
        viewModelScope.launch { repository.markRead(withUser) }
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }

    private fun Throwable.readable(): String = when (this) {
        is MessageError.UnknownRecipient -> "No user called $username"
        is MessageError.TooLong -> "Message is too long"
        is MessageError.NotAuthenticated -> "Session expired. Please log in again."
        is MessageError.Offline -> "Could not reach the server"
        else -> message ?: "Something went wrong"
    }
}
